import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import { ChevronLeftIcon } from '@/assets/icon/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icon/ChevronRightIcon';
import brandLogoImage from '@/assets/images/brand-logo.png';
import popularBrandsIcon from '@/assets/images/popular-brands.png';
import NavigationButton from '@/components/atoms/NavigationButton/NavigationButton';
import { PopularBrandsProps } from '@/types';

import SearchSectionHeader from '../SearchSectionHeader/SearchSectionHeader';
import {
  BrandItemStyle,
  BrandRow,
  BrandsScrollContainer,
  BrandsWrapper,
  BrandWrapperStyle,
  PopularBrandContainer,
} from './PopularBrands.style';

const PopularBrands: React.FC<PopularBrandsProps> = ({
  popularBrands,
  isNative,
  onClickBrand,
  isWebMobileView = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!isNative && scrollContainerRef?.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth);
    }
  }, [isNative]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();

    const resizeObserver = new ResizeObserver(() => checkScrollPosition());
    resizeObserver.observe(container);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [popularBrands, checkScrollPosition]);
  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const isSingleRow = popularBrands.length <= 9;
  const colCount = isSingleRow
    ? popularBrands.length
    : Math.ceil(popularBrands.length / 2);

  const showButtons = !isNative && !isWebMobileView;

  return (
    <PopularBrandContainer>
      <SearchSectionHeader
        title={t('search.popularBrands')}
        titleIcon={popularBrandsIcon.src}
      />

      <BrandsWrapper>
        {showButtons && (
          <NavigationButton
            position="left"
            onClick={scrollLeft}
            aria-label="Scroll left"
            style={{
              display: canScrollLeft ? 'flex' : 'none',
            }}
          >
            <ChevronLeftIcon width={32} height={32} />
          </NavigationButton>
        )}
        <BrandsScrollContainer
          ref={scrollContainerRef}
          onScroll={checkScrollPosition}
        >
          <BrandRow isSingleRow={isSingleRow} colCount={colCount}>
            {popularBrands.map((brands, idx) => (
              <BrandWrapperStyle
                key={idx}
                onClick={() => onClickBrand(brands, idx)}
              >
                <BrandItemStyle
                  src={brands?.image || brandLogoImage.src}
                  alt={`brand-${idx}`}
                  width={Number.parseInt(theme.spacing(8), 10) || 64}
                  height={Number.parseInt(theme.spacing(8), 10) || 64}
                  sizes="64px"
                  priority
                  loading="eager"
                  fetchPriority="high"
                  quality={75}
                />
              </BrandWrapperStyle>
            ))}
          </BrandRow>
        </BrandsScrollContainer>
        {showButtons && (
          <NavigationButton
            position="right"
            onClick={scrollRight}
            aria-label="Scroll right"
            style={{
              display: canScrollRight ? 'flex' : 'none',
            }}
          >
            <ChevronRightIcon width={32} height={32} />
          </NavigationButton>
        )}
      </BrandsWrapper>
    </PopularBrandContainer>
  );
};

export default PopularBrands;
